import React, { useState, useRef, useEffect } from "react";
import { Input, Button, CheckBox, UserIco, PhoneIco, MailIco, ChronicIco } from "@speedoc/design-assets";
import ContainerCard from "./containerCard";
import { trackCustom } from "../../services/analytics";
import SubmissionConfirmation from "./submissionConfirmation";
import { GlobalStore } from "../../stores/global";
import { Chevron } from "@speedoc/design-assets/build/icons/index";
import { useMediaQuery } from "react-responsive";
import axios from "axios";

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = event => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

const Select = ({
  title,
  list,
  name,
  required = false,
  formId,
  autofocus = false,
  size = "300px",
  disabled,
  blurType = "",
  id,
  className = "",
  onChange,
  selectedItems = []
}) => {
  selectedItems = selectedItems || [];
  const [initialState, setInitialState] = useState({
    listOpen: false,
    headerTitle: selectedItems.map(item => item.title).toString() || title,
    selectedItems: [],
    blurType: blurType
  });

  const refSelectedValueInput = useRef();
  const refWrapper = useRef();

  const close = () => {
    setInitialState({
      ...initialState,
      listOpen: false
    });
  };

  useOnClickOutside(refWrapper, () => close());

  const toggleList = () => {
    if (!disabled && list.length > 0) {
      setInitialState(prevState => ({
        ...prevState,
        blurType: "",
        listOpen: !prevState.listOpen
      }));
    }
  };

  const selectItem = (item, isSelect) => {
    let itemIndex = null;
    for (let i = 0; i < initialState.selectedItems?.length; i++) {
      if (initialState.selectedItems[i].id === item.id) {
        itemIndex = i;
        break;
      }
    }

    if (!isSelect && itemIndex != null) {
      initialState.selectedItems.splice(itemIndex, 1);
    }

    if (!!isSelect && itemIndex == null) {
      initialState.selectedItems.push(item);
    }

    onChange && onChange([].concat(initialState.selectedItems));
    setInitialState({
      listOpen: true,
      headerTitle: initialState.selectedItems.map(selectedItem => selectedItem.title).toString() || title,
      selectedItems: [].concat(initialState.selectedItems)
    });

    refSelectedValueInput.current.value = initialState.selectedItems.map(selectedItem => selectedItem.id).toString();
  };

  return (
    <div
      className={initialState.listOpen ? `select-wrapper opened ${className}` : `select-wrapper ${className}`}
      ref={refWrapper}
      disabled={!list.length || list.length === 0 || disabled}
      name={name}
      required={required}>
      <button
        type="button"
        className={`select-header ${initialState.blurType} ${className}`}
        onClick={() => toggleList()}
        autoFocus={autofocus}>
        <div
          className={
            "select-header-title" + (initialState.selectedItems?.length ? " true hasSelectedItems" : " false")
          }>
          {initialState.headerTitle}
        </div>
        <Chevron />
      </button>
      <div className={"icon"}>
        <ChronicIco />
      </div>
      <select id="hiddenSelect" required={required}>
        {initialState.selectedItem && <option value="selected">0</option>}
      </select>
      {initialState.listOpen && (
        <div
          role="list"
          className="select-list"
          onClick={e => e.stopPropagation()}
          style={{ maxHeight: size, overflowY: "auto" }}>
          <div className="select-scroll-list">
            {list.map(item => {
              let checked = !!initialState.selectedItems.filter(it => it.id === item.id)[0];
              return (
                <button
                  type="button"
                  className={"select-list-item " + checked}
                  key={item.id}
                  onClick={() => {
                    clearTimeout(initialState.to);
                    initialState.to = setTimeout(() => {
                      selectItem(item, !checked);
                    }, 100);
                  }}>
                  <CheckBox required id="conditions" defaultChecked={checked} title={item.title} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      <input type="hidden" id={id} ref={refSelectedValueInput} form={formId} defaultValue={initialState.selectedItem} />
    </div>
  );
};

export default function ContactFormChronicV2({ content }) {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { currentCountryCode: countryCode } = GlobalStore.useState(c => c);
  const { submissionSuccessText, chronicConditions, formUrl } = content;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formHeight, setFormHeight] = useState(undefined);
  const targetRef = useRef();
  const submitRef = useRef(null);
  const [error, setError] = useState(false);
  const [idSuffix] = useState(Date.now());

  const initialState = {
    email: "",
    name: "",
    phone: "",
    chronic: ""
  };

  const [formData, setFormData] = useState(initialState);

  const handleInputChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.phone || isNaN(formData.phone) || formData.phone.length < 8 || formData.phone.length > 11) {
      setError("Please enter a valid phone number.");
      setIsSubmitting(false);
    } else if (formData.email.toLowerCase().match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/)) {
      try {
        const submitForm = new FormData();
        submitForm.append("q4_typeA4", formData.name);
        submitForm.append("q5_typeA5", formData.email);
        submitForm.append("q6_typeA6", formData.phone);
        submitForm.append("q7_typeA7", formData.chronic);
        await axios.post(formUrl, submitForm, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setFormHeight(targetRef.current.offsetHeight);
      } catch (err) {
        console.log(err);
      } finally {
        trackCustom("Lead", countryCode, { email: formData.email });
        setError(false);
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData({ ...initialState });
      }
    } else {
      setError("Please enter a valid e-mail address.");
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    setIsSubmitted(false);
  };
  return !isMobile ? (
    <div id={"contactFormChronicV2"}>
      {!isSubmitted ? (
        <div className={"container"} ref={targetRef}>
          <ContainerCard>
            <form className="formSection" onSubmit={handleSubmit} ref={submitRef}>
              <div className="form-group">
                <label>Full Name</label>
                <Input
                  className="sm"
                  icon={<UserIco />}
                  id={"name-" + idSuffix}
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className={"emailPhoneContainer"}>
                <div className="form-group phone">
                  <label>Phone</label>
                  <Input
                    className="sm"
                    icon={<PhoneIco />}
                    id={"phone-" + idSuffix}
                    name="phone"
                    placeholder="Mobile Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="separate" />
                <div className="form-group email">
                  <label name="emailLabel">Email</label>
                  <Input
                    className={`sm ${error && error.includes("email") ? "error" : ""}`}
                    icon={<MailIco />}
                    id={"email-" + idSuffix}
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="solid" />
              <div className={"conditionContainer"}>
                <div className="formGroupChronic">
                  {chronicConditions && chronicConditions.length > 0 && (
                    <>
                      <label name="chronicHeader">Chronic Condition</label>
                      <Select
                        required
                        name="chronic"
                        title="Select"
                        defaultValue={null}
                        list={chronicConditions}
                        onChange={v => {
                          setFormData({ ...formData, chronic: v.map(it => it.id).toString() });
                        }}
                        className="sm"
                      />
                    </>
                  )}
                </div>
                <div className="buttonSection">
                  <Button onClick={handleSubmit} disabled={isSubmitting} className="primary sm">
                    {isSubmitting ? "Please wait..." : content.buttonText || "Submit request"}
                  </Button>
                  {error ? <p className="errorMessage">{error}</p> : null}
                </div>
              </div>
            </form>
          </ContainerCard>
        </div>
      ) : (
        <SubmissionConfirmation
          data={{ submissionSuccessText }}
          formHeight={formHeight}
          handleBackClick={handleBackClick}
        />
      )}
    </div>
  ) : (
    <div id={"contactFormChronicV2"}>
      {!isSubmitted ? (
        <div className={"container"} ref={targetRef}>
          <ContainerCard>
            <form className="formSection" onSubmit={handleSubmit} ref={submitRef}>
              <div className="form-group">
                <label>Full Name</label>
                <Input
                  className="sm"
                  icon={<UserIco />}
                  id={"name-" + idSuffix}
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <Input
                  className="sm"
                  icon={<PhoneIco />}
                  id={"phone-" + idSuffix}
                  name="phone"
                  placeholder="Mobile Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label name="emailLabel">Email</label>
                <Input
                  className={`sm ${error && error.includes("email") ? "error" : ""}`}
                  icon={<MailIco />}
                  id={"email-" + idSuffix}
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className={"conditionContainer"}>
                <div className="formGroupChronic">
                  {chronicConditions && chronicConditions.length > 0 && (
                    <>
                      <label name="chronicHeader">Chronic Condition</label>
                      <Select
                        required
                        name="chronic"
                        title="Select"
                        defaultValue={null}
                        list={chronicConditions}
                        onChange={v => {
                          setFormData({ ...formData, chronic: v.map(it => it.id).toString() });
                        }}
                        className="sm"
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="buttonSection">
                <Button onClick={handleSubmit} disabled={isSubmitting} className="primary sm">
                  {isSubmitting ? "Please wait..." : content.buttonText || "Submit request"}
                </Button>
                {error ? <p className="errorMessage">{error}</p> : null}
              </div>
            </form>
          </ContainerCard>
        </div>
      ) : (
        <SubmissionConfirmation
          data={{ submissionSuccessText }}
          formHeight={formHeight}
          handleBackClick={handleBackClick}
        />
      )}
    </div>
  );
}
